import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import FundService from "../../services/fund.service";

/* =====================================================
   GET MY FUND TRANSFERS
===================================================== */

export const getMyFundTransfers = createAsyncThunk(
  "fund/getMyFundTransfers",
  async (params, thunkAPI) => {
    try {
      const response =
        await FundService.getMyFundTransfers(params);

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        error.message ||
        "Failed to fetch fund transfers"
      );
    }
  }
);

/* =====================================================
   GET REFERRAL INCOME
===================================================== */

export const getReferralIncome = createAsyncThunk(
  "fund/getReferralIncome",
  async (params, thunkAPI) => {
    try {
      const response =
        await FundService.getReferralIncome(params);

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        error.message ||
        "Failed to fetch referral income"
      );
    }
  }
);

/* =====================================================
   GET MY WITHDRAWALS
===================================================== */

export const getMyWithdrawals = createAsyncThunk(
  "fund/getMyWithdrawals",
  async (params, thunkAPI) => {
    try {
      const response =
        await FundService.getMyWithdrawals(params);

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        error.message ||
        "Failed to fetch withdrawals"
      );
    }
  }
);

/* =====================================================
   GET AUTO POOL INCOME
===================================================== */

export const getAutoPoolIncome = createAsyncThunk(
  "fund/getAutoPoolIncome",
  async (params, thunkAPI) => {
    try {
      const response =
        await FundService.getAutoPoolIncome(params);

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        error.message ||
        "Failed to fetch auto pool income"
      );
    }
  }
);

/* =====================================================
   GET USER DETAILS BY USER ID
===================================================== */

export const getUserDetailsByUserId = createAsyncThunk(
  "fund/getUserDetailsByUserId",
  async (params, thunkAPI) => {
    try {
      const response =
        await FundService.getUserDetailsByUserId(params);

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        error.message ||
        "Failed to fetch user details"
      );
    }
  }
);

/* =====================================================
   GET MY SUPPORT INCOME
===================================================== */

export const getMySupportIncome = createAsyncThunk(
  "fund/getMySupportIncome",
  async (params, thunkAPI) => {
    try {
      const response =
        await FundService.getMySupportIncome(params);

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        error.message ||
        "Failed to fetch support income"
      );
    }
  }
);

/* =====================================================
   TRANSFER FUND
===================================================== */

export const transferFund = createAsyncThunk(
  "fund/transferFund",
  async (params, thunkAPI) => {
    try {
      const response =
        await FundService.transferFund(params);

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        error.message ||
        "Failed to transfer fund"
      );
    }
  }
);

/* =====================================================
   CREATE WITHDRAWAL REQUEST
===================================================== */

export const createWithdrawalRequest = createAsyncThunk(
  "fund/createWithdrawalRequest",
  async (params, thunkAPI) => {
    try {
      const response =
        await FundService.createWithdrawalRequest(params);

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        error.message ||
        "Failed to create withdrawal request"
      );
    }
  }
);

/* =====================================================
   INITIAL STATE
===================================================== */

const initialState = {
  /* ================= COMMON ================= */

  loading: false,
  error: null,

  /* ================= FUND TRANSFERS ================= */

  fundTransfers: [],
  fundTransfersCount: 0,
  fundTransfersLoading: false,
  fundTransfersError: null,

  /* ================= REFERRAL INCOME ================= */

  referralIncome: [],
  referralIncomeTotal: 0,
  referralIncomeCount: 0,
  referralIncomeLoading: false,
  referralIncomeError: null,

  /* ================= AUTO POOL INCOME ================= */

  autoPoolIncome: {
    records: [],
    totalIncome: 0,
  },

  autoPoolIncomeTotal: 0,
  autoPoolIncomeCount: 0,
  autoPoolIncomeLoading: false,
  autoPoolIncomeError: null,

  /* ================= SUPPORT INCOME ================= */

  supportIncome: {
    records: [],
    pagination: {
      totalRecords: 0,
      totalPages: 0,
      currentPage: 1,
      limit: 20,
    },
    totalAmount: 0,
  },

  supportIncomeTotal: 0,
  supportIncomeCount: 0,
  supportIncomeLoading: false,
  supportIncomeError: null,

  /* ================= WITHDRAWALS ================= */

  withdrawals: [],
  withdrawalsCount: 0,
  withdrawalsLoading: false,
  withdrawalsError: null,

  /* ================= USER DETAILS ================= */

  userDetails: null,
  userDetailsLoading: false,
  userDetailsError: null,

  /* ================= TRANSFER FUND ================= */

  transferLoading: false,
  transferSuccess: false,
  transferData: null,
  transferError: null,

  /* ================= WITHDRAWAL REQUEST ================= */

  withdrawalRequestLoading: false,
  withdrawalRequestSuccess: false,
  withdrawalRequestData: null,
  withdrawalRequestError: null,
};

/* =====================================================
   SLICE
===================================================== */

const fundSlice = createSlice({
  name: "fund",

  initialState,

  reducers: {
    /* =========================
       RESET TRANSFER STATUS
    ========================= */

    resetTransferStatus: (state) => {
      state.transferSuccess = false;
      state.transferData = null;
      state.transferError = null;
    },

    /* =========================
       RESET WITHDRAWAL STATUS
    ========================= */

    resetWithdrawalRequestStatus: (state) => {
      state.withdrawalRequestLoading = false;
      state.withdrawalRequestSuccess = false;
      state.withdrawalRequestData = null;
      state.withdrawalRequestError = null;
    },

    /* =========================
       CLEAR USER DETAILS
    ========================= */

    clearUserDetails: (state) => {
      state.userDetails = null;
      state.userDetailsError = null;
    },

    /* =========================
       CLEAR REFERRAL INCOME
    ========================= */

    clearReferralIncome: (state) => {
      state.referralIncome = [];
      state.referralIncomeTotal = 0;
      state.referralIncomeCount = 0;
      state.referralIncomeError = null;
    },

    /* =========================
       CLEAR AUTO POOL INCOME
    ========================= */

    clearAutoPoolIncome: (state) => {
      state.autoPoolIncome = {
        records: [],
        totalIncome: 0,
      };

      state.autoPoolIncomeTotal = 0;
      state.autoPoolIncomeCount = 0;
      state.autoPoolIncomeError = null;
    },

    /* =========================
       CLEAR SUPPORT INCOME
    ========================= */

    clearSupportIncome: (state) => {
      state.supportIncome = {
        records: [],
        pagination: {
          totalRecords: 0,
          totalPages: 0,
          currentPage: 1,
          limit: 20,
        },
        totalAmount: 0,
      };

      state.supportIncomeTotal = 0;
      state.supportIncomeCount = 0;
      state.supportIncomeError = null;
    },

    /* =========================
       CLEAR WITHDRAWALS
    ========================= */

    clearWithdrawals: (state) => {
      state.withdrawals = [];
      state.withdrawalsCount = 0;
      state.withdrawalsError = null;
    },

    /* =========================
       CLEAR ERROR
    ========================= */

    clearFundError: (state) => {
      state.error = null;
      state.fundTransfersError = null;
      state.referralIncomeError = null;
      state.autoPoolIncomeError = null;
      state.supportIncomeError = null;
      state.withdrawalsError = null;
      state.userDetailsError = null;
      state.transferError = null;
      state.withdrawalRequestError = null;
    },
  },

  /* =====================================================
     EXTRA REDUCERS
  ===================================================== */

  extraReducers: (builder) => {
    builder

      /* =====================================================
         GET MY FUND TRANSFERS
      ===================================================== */

      .addCase(
        getMyFundTransfers.pending,
        (state) => {
          state.fundTransfersLoading = true;
          state.fundTransfersError = null;
          state.error = null;
        }
      )

      .addCase(
        getMyFundTransfers.fulfilled,
        (state, action) => {
          state.fundTransfersLoading = false;
          state.fundTransfersError = null;
          state.error = null;

          const data =
            action.payload?.data;

          if (Array.isArray(data)) {
            state.fundTransfers = data;
            state.fundTransfersCount =
              data.length;
          } else {
            state.fundTransfers =
              data?.transfers ||
              data?.fundTransfers ||
              data?.transactions ||
              [];

            state.fundTransfersCount =
              data?.total ??
              data?.count ??
              data?.pagination?.total ??
              state.fundTransfers.length;
          }
        }
      )

      .addCase(
        getMyFundTransfers.rejected,
        (state, action) => {
          state.fundTransfersLoading = false;

          state.fundTransfersError =
            action.payload ||
            "Failed to fetch fund transfers";

          state.error =
            action.payload ||
            "Failed to fetch fund transfers";

          state.fundTransfers = [];
          state.fundTransfersCount = 0;
        }
      )

      /* =====================================================
         GET REFERRAL INCOME
      ===================================================== */

      .addCase(
        getReferralIncome.pending,
        (state) => {
          state.referralIncomeLoading = true;
          state.referralIncomeError = null;
          state.error = null;
        }
      )

      .addCase(
        getReferralIncome.fulfilled,
        (state, action) => {
          state.referralIncomeLoading = false;
          state.referralIncomeError = null;
          state.error = null;

          const data =
            action.payload?.data || {};

          state.referralIncome =
            Array.isArray(
              data.referralIncome
            )
              ? data.referralIncome
              : [];

          state.referralIncomeTotal =
            data.totalReferralIncome ?? 0;

          state.referralIncomeCount =
            data.count ??
            state.referralIncome.length;
        }
      )

      .addCase(
        getReferralIncome.rejected,
        (state, action) => {
          state.referralIncomeLoading = false;

          state.referralIncomeError =
            action.payload ||
            "Failed to fetch referral income";

          state.error =
            action.payload ||
            "Failed to fetch referral income";

          state.referralIncome = [];
          state.referralIncomeTotal = 0;
          state.referralIncomeCount = 0;
        }
      )

      /* =====================================================
         GET AUTO POOL INCOME
      ===================================================== */

      .addCase(
        getAutoPoolIncome.pending,
        (state) => {
          state.autoPoolIncomeLoading = true;
          state.autoPoolIncomeError = null;
          state.error = null;
        }
      )

      .addCase(
        getAutoPoolIncome.fulfilled,
        (state, action) => {
          state.autoPoolIncomeLoading = false;
          state.autoPoolIncomeError = null;
          state.error = null;

          const data =
            action.payload?.data || {};

          const records =
            Array.isArray(data.records)
              ? data.records
              : [];

          state.autoPoolIncome = {
            records,
            totalIncome:
              Number(data.totalIncome) || 0,
          };

          state.autoPoolIncomeTotal =
            Number(data.totalIncome) || 0;

          state.autoPoolIncomeCount =
            records.length;
        }
      )

      .addCase(
        getAutoPoolIncome.rejected,
        (state, action) => {
          state.autoPoolIncomeLoading = false;

          state.autoPoolIncomeError =
            action.payload ||
            "Failed to fetch auto pool income";

          state.error =
            action.payload ||
            "Failed to fetch auto pool income";

          state.autoPoolIncome = {
            records: [],
            totalIncome: 0,
          };

          state.autoPoolIncomeTotal = 0;
          state.autoPoolIncomeCount = 0;
        }
      )

      /* =====================================================
         GET MY SUPPORT INCOME
      ===================================================== */

      .addCase(
        getMySupportIncome.pending,
        (state) => {
          state.supportIncomeLoading = true;
          state.supportIncomeError = null;
          state.error = null;
        }
      )

      .addCase(
        getMySupportIncome.fulfilled,
        (state, action) => {
          state.supportIncomeLoading = false;
          state.supportIncomeError = null;
          state.error = null;

          const data =
            action.payload?.data || {};

          const records =
            Array.isArray(data.records)
              ? data.records
              : [];

          state.supportIncome = {
            records,
            pagination:
              data.pagination || {
                totalRecords: records.length,
                totalPages: 1,
                currentPage: 1,
                limit: records.length,
              },
            totalAmount:
              Number(data.totalAmount) || 0,
          };

          state.supportIncomeTotal =
            Number(data.totalAmount) || 0;

          state.supportIncomeCount =
            data.pagination?.totalRecords ??
            records.length;
        }
      )

      .addCase(
        getMySupportIncome.rejected,
        (state, action) => {
          state.supportIncomeLoading = false;

          state.supportIncomeError =
            action.payload ||
            "Failed to fetch support income";

          state.error =
            action.payload ||
            "Failed to fetch support income";

          state.supportIncome = {
            records: [],
            pagination: {
              totalRecords: 0,
              totalPages: 0,
              currentPage: 1,
              limit: 20,
            },
            totalAmount: 0,
          };

          state.supportIncomeTotal = 0;
          state.supportIncomeCount = 0;
        }
      )

      /* =====================================================
         GET MY WITHDRAWALS
      ===================================================== */

      .addCase(
        getMyWithdrawals.pending,
        (state) => {
          state.withdrawalsLoading = true;
          state.withdrawalsError = null;
          state.error = null;
        }
      )

      .addCase(
        getMyWithdrawals.fulfilled,
        (state, action) => {
          state.withdrawalsLoading = false;
          state.withdrawalsError = null;
          state.error = null;

          const data =
            action.payload?.data;

          if (Array.isArray(data)) {
            state.withdrawals = data;
            state.withdrawalsCount =
              data.length;
          } else {
            state.withdrawals =
              data?.withdrawals ||
              data?.transactions ||
              data?.requests ||
              [];

            state.withdrawalsCount =
              data?.total ??
              data?.count ??
              data?.pagination?.total ??
              state.withdrawals.length;
          }
        }
      )

      .addCase(
        getMyWithdrawals.rejected,
        (state, action) => {
          state.withdrawalsLoading = false;

          state.withdrawalsError =
            action.payload ||
            "Failed to fetch withdrawals";

          state.error =
            action.payload ||
            "Failed to fetch withdrawals";

          state.withdrawals = [];
          state.withdrawalsCount = 0;
        }
      )

      /* =====================================================
         GET USER DETAILS BY USER ID
      ===================================================== */

      .addCase(
        getUserDetailsByUserId.pending,
        (state) => {
          state.userDetailsLoading = true;
          state.userDetailsError = null;
          state.error = null;

          state.userDetails = null;
        }
      )

      .addCase(
        getUserDetailsByUserId.fulfilled,
        (state, action) => {
          state.userDetailsLoading = false;
          state.userDetailsError = null;

          state.userDetails =
            action.payload?.data ||
            null;
        }
      )

      .addCase(
        getUserDetailsByUserId.rejected,
        (state, action) => {
          state.userDetailsLoading = false;

          state.userDetailsError =
            action.payload ||
            "Failed to fetch user details";

          state.error =
            action.payload ||
            "Failed to fetch user details";

          state.userDetails = null;
        }
      )

      /* =====================================================
         TRANSFER FUND
      ===================================================== */

      .addCase(
        transferFund.pending,
        (state) => {
          state.transferLoading = true;
          state.transferSuccess = false;
          state.transferData = null;
          state.transferError = null;
          state.error = null;
        }
      )

      .addCase(
        transferFund.fulfilled,
        (state, action) => {
          state.transferLoading = false;
          state.transferSuccess = true;
          state.transferError = null;

          state.transferData =
            action.payload?.data ||
            null;
        }
      )

      .addCase(
        transferFund.rejected,
        (state, action) => {
          state.transferLoading = false;
          state.transferSuccess = false;

          state.transferError =
            action.payload ||
            "Failed to transfer fund";

          state.error =
            action.payload ||
            "Failed to transfer fund";

          state.transferData = null;
        }
      )

      /* =====================================================
         CREATE WITHDRAWAL REQUEST
      ===================================================== */

      .addCase(
        createWithdrawalRequest.pending,
        (state) => {
          state.withdrawalRequestLoading = true;
          state.withdrawalRequestSuccess = false;
          state.withdrawalRequestData = null;
          state.withdrawalRequestError = null;
          state.error = null;
        }
      )

      .addCase(
        createWithdrawalRequest.fulfilled,
        (state, action) => {
          state.withdrawalRequestLoading = false;
          state.withdrawalRequestSuccess = true;
          state.withdrawalRequestError = null;

          state.withdrawalRequestData =
            action.payload?.data ||
            null;
        }
      )

      .addCase(
        createWithdrawalRequest.rejected,
        (state, action) => {
          state.withdrawalRequestLoading = false;
          state.withdrawalRequestSuccess = false;

          state.withdrawalRequestError =
            action.payload ||
            "Failed to create withdrawal request";

          state.error =
            action.payload ||
            "Failed to create withdrawal request";

          state.withdrawalRequestData = null;
        }
      );
  },
});

/* =====================================================
   ACTIONS
===================================================== */

export const {
  resetTransferStatus,
  resetWithdrawalRequestStatus,
  clearUserDetails,
  clearReferralIncome,
  clearAutoPoolIncome,
  clearSupportIncome,
  clearWithdrawals,
  clearFundError,
} = fundSlice.actions;

/* =====================================================
   REDUCER
===================================================== */

export default fundSlice.reducer;